import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Modal,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Stack,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import {
  Add,
  Visibility,
  Person,
  Comment,
  Schedule,
  CalendarToday,
  CheckCircle,
  PendingActions,
  Close,
} from "@mui/icons-material";
import { createTicket, getAllTickets, getAllUsers } from "../services/api";
import { toast, ToastContainer } from "react-toastify";
import { convertToRawDate } from "../shared/base";

// Dummy data
const initialTickets = [
  {
    id: 1,
    Title: "Website Redesign Support",
    Description: "Need UX designer for client website overhaul",
    status: "Pending Approval",
    Priority: "High",
    requester: "Sales Team B",
    Assignee: { id: 3, name: "Mike Chen", department: "Design" },
    ExpectedStartDate: "2023-07-01",
    ExpectedEndDate: "2023-07-15",
    actualStart: null,
    actualEnd: null,
    LeadTime: "2 weeks",
    createdAt: "2023-06-10",
    approvalHistory: [],
  },
];

const assigneeOptions = [
  { id: 1, name: "John Doe", department: "Sales" },
  { id: 2, name: "Jane Smith", department: "Marketing" },
  { id: 3, name: "Mike Chen", department: "Design" },
];

const UnresolvedTickets = () => {
  const [tickets, setTickets] = useState(initialTickets);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newTicket, setNewTicket] = useState({
    Title: "",
    Description: "",
    Priority: "Medium",
    Assignee: "",
    ExpectedStartDate: new Date().toISOString().split("T")[0],
    ExpectedEndDate: "",
    LeadTime: "",
    TicketStatus: "Pending",
    Category: "",
  });
  const [assignees, setAssignees] = useState([]);

  const _getAllUsers = async () => {
    const getAllUsersApi = await getAllUsers();
    if (getAllUsersApi && getAllUsersApi.success) {
      setAssignees(getAllUsersApi.data);
    }
  };

  const _getAllTickets = async () => {
    const getAllTicketsApi = await getAllTickets({
      FilterField: "TicketStatus",
      Value: "Pending",
    });
    if (getAllTicketsApi && getAllTicketsApi.success) {
      setTickets(getAllTicketsApi.data);
    }
  };

  useEffect(() => {
    _getAllUsers();
    _getAllTickets();
  }, []);

  const handleOpenDetails = (ticket) => setSelectedTicket(ticket);
  const handleCloseDetails = () => setSelectedTicket(null);

  const handleCreateSubmit = async () => {
    const ticket = {
      ...newTicket,
      Assignee: newTicket.Assignee,
    };

    const createdTicket = await createTicket(ticket);
    if (createdTicket && createdTicket.success) {
      toast.success("Ticket successfully submitted.");
      setCreateModalOpen(false);
      resetForm();
      _getAllTickets();
    } else {
      toast.error(createdTicket.message);
    }
  };

  const resetForm = () => {
    setNewTicket({
      Title: "",
      Description: "",
      Priority: "Medium",
      Assignee: "",
      ExpectedStartDate: new Date().toISOString().split("T")[0],
      ExpectedEndDate: "",
      LeadTime: "",
      TicketStatus: "Pending",
      Category: "",
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <ToastContainer />
      {/* Header with Create Button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" color="#d32f2f">
          Unresolved Tickets
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateModalOpen(true)}
          color="error"
        >
          New Ticket
        </Button>
      </Box>

      {/* Tickets Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: "#ffebee" }}>
            <TableRow>
              <TableCell>Ticket</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Assignee</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Requested Dates</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tickets.map((ticket) => (
              <TableRow key={ticket.ID} hover>
                <TableCell>
                  <Typography fontWeight="bold">{ticket.Title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {ticket.Description.substring(0, 50)}...
                  </Typography>
                  <Chip
                    label={ticket.Priority}
                    size="small"
                    color={
                      ticket.Priority === "High"
                        ? "error"
                        : ticket.Priority === "Medium"
                        ? "warning"
                        : "success"
                    }
                    sx={{ mt: 1 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={ticket.TicketStatus}
                    color={
                      ticket.TicketStatus === "Pending"
                        ? "warning"
                        : ticket.TicketStatus === "In Progress"
                        ? "primary"
                        : "default"
                    }
                    icon={
                      ticket.TicketStatus === "Pending" ? (
                        <PendingActions />
                      ) : ticket.TicketStatus === "In Progress" ? (
                        <Schedule />
                      ) : (
                        <CheckCircle />
                      )
                    }
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        mr: 1,
                        bgcolor:
                          ticket?._Assignee?._Role === "Sales"
                            ? "#d32f2f"
                            : "#7b1fa2",
                      }}
                    >
                      {ticket?._Assignee?.FirstName.charAt(0)}
                    </Avatar>
                    {ticket?._Assignee?.FirstName}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{ticket?.Category}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {convertToRawDate(ticket.ExpectedStartDate)} to{" "}
                    {convertToRawDate(ticket.ExpectedEndDate)}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {ticket.LeadTime}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => handleOpenDetails(ticket)}
                    color="error"
                    size="small"
                  >
                    Details
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* CREATE TICKET MODAL */}
      <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "80%", md: "700px" },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h5" color="#d32f2f">
              Create New Ticket
            </Typography>
            <IconButton onClick={() => setCreateModalOpen(false)} color="error">
              <Close />
            </IconButton>
          </Box>

          <Box component="form" sx={{ mt: 2 }}>
            {/* Category - Full width */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Category*</InputLabel>
              <Select
                value={newTicket.Category}
                label="Category*"
                onChange={(e) =>
                  setNewTicket({ ...newTicket, Category: e.target.value })
                }
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300, // Limit dropdown height
                    },
                  },
                }}
              >
                {[
                  {
                    category: "Technical Issues",
                    description:
                      "Problems with tools, equipment, systems, or processes that prevent normal operations.",
                  },
                  {
                    category: "Service Requests",
                    description:
                      "Requests for new services, resources, assistance, or changes to existing arrangements.",
                  },
                  {
                    category: "Operational",
                    description:
                      "Matters related to day-to-day business processes, coordination, or policy inquiries.",
                  },
                  {
                    category: "Incidents",
                    description:
                      "Unplanned events or disruptions that require immediate attention to restore normal conditions.",
                  },
                ].map((category, index) => (
                  <MenuItem key={index} value={category.category}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          mr: 1.5,
                          fontSize: "0.8rem",
                        }}
                      >
                        {category.category?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography>{category.category}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {category.description}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Title - Full width */}
            <TextField
              fullWidth
              label="Title*"
              value={newTicket.Title}
              onChange={(e) =>
                setNewTicket({ ...newTicket, Title: e.target.value })
              }
              sx={{ mb: 3 }}
            />

            {/* Description - Full width */}
            <TextField
              fullWidth
              label="Description*"
              multiline
              rows={4}
              value={newTicket.Description}
              onChange={(e) =>
                setNewTicket({ ...newTicket, Description: e.target.value })
              }
              sx={{ mb: 3 }}
            />

            {/* Priority and Dates - Inline on desktop */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Priority*</InputLabel>
                  <Select
                    value={newTicket.Priority}
                    label="Priority*"
                    onChange={(e) =>
                      setNewTicket({ ...newTicket, Priority: e.target.value })
                    }
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Expected Start*"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={newTicket.ExpectedStartDate}
                  onChange={(e) =>
                    setNewTicket({
                      ...newTicket,
                      ExpectedStartDate: e.target.value,
                    })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Expected End*"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                  value={newTicket.ExpectedEndDate}
                  onChange={(e) =>
                    setNewTicket({
                      ...newTicket,
                      ExpectedEndDate: e.target.value,
                    })
                  }
                />
              </Grid>
            </Grid>

            {/* Assignee - Full width */}
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Assignee*</InputLabel>
              <Select
                value={newTicket.Assignee}
                label="Assignee*"
                onChange={(e) =>
                  setNewTicket({ ...newTicket, Assignee: e.target.value })
                }
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300, // Limit dropdown height
                    },
                  },
                }}
              >
                {assignees.map((Assignee) => (
                  <MenuItem key={Assignee.ID} value={Assignee.ID}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          mr: 1.5,
                          fontSize: "0.8rem",
                          bgcolor:
                            Assignee?._Role.Name === "Sales"
                              ? "#d32f2f"
                              : "#7b1fa2",
                        }}
                      >
                        {Assignee?.FirstName?.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography>{Assignee.FirstName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Assignee._Role.Name}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                pt: 2,
              }}
            >
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  setCreateModalOpen(false);
                  resetForm();
                }}
                sx={{ minWidth: 100 }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleCreateSubmit}
                disabled={
                  !newTicket.Title ||
                  !newTicket.Description ||
                  !newTicket.Assignee
                }
                sx={{ minWidth: 140 }}
              >
                Create Ticket
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* TICKET DETAILS MODAL */}
      <Modal open={Boolean(selectedTicket)} onClose={handleCloseDetails}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: "70%" },
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          {selectedTicket && (
            <>
              <Typography variant="h5" gutterBottom color="#d32f2f">
                {selectedTicket.Title}
              </Typography>

              <Typography variant="body1" paragraph>
                {selectedTicket.Description}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Grid container spacing={3}>
                {/* Left Column - Ticket Info */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom color="#d32f2f">
                    Ticket Information
                  </Typography>

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2">Requester</Typography>
                      <Typography>{`${selectedTicket?._Reporter?.FirstName} ${selectedTicket?._Reporter?.LastName}`}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2">Assignee</Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mt: 1 }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1,
                            bgcolor:
                              selectedTicket?._Assignee?._Role.Name === "Sales"
                                ? "#d32f2f"
                                : "#7b1fa2",
                          }}
                        >
                          {selectedTicket?._Assignee?.FirstName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography>
                            {`${selectedTicket?._Assignee?.FirstName} ${selectedTicket?._Assignee?.LastName}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedTicket?.Assignee?._Role?.Name}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="subtitle2">Category</Typography>
                      <Chip label={selectedTicket.Category} color={"success"} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2">Priority</Typography>
                      <Chip
                        label={selectedTicket.Priority}
                        color={
                          selectedTicket.Priority === "High"
                            ? "error"
                            : selectedTicket.Priority === "Medium"
                            ? "warning"
                            : "success"
                        }
                      />
                    </Box>

                    <Box>
                      <Typography variant="subtitle2">Status</Typography>
                      <Chip
                        label={selectedTicket.TicketStatus}
                        color={
                          selectedTicket.TicketStatus === "Pending"
                            ? "warning"
                            : selectedTicket.TicketStatus === "In Progress"
                            ? "primary"
                            : "success"
                        }
                        icon={
                          selectedTicket.TicketStatus === "Pending" ? (
                            <PendingActions />
                          ) : selectedTicket.TicketStatus === "In Progress" ? (
                            <Schedule />
                          ) : (
                            <CheckCircle />
                          )
                        }
                      />
                    </Box>
                  </Stack>
                </Grid>

                {/* Right Column - Timeline */}
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom color="#d32f2f">
                    Timeline
                  </Typography>

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2">
                        Requested Dates
                      </Typography>
                      <Typography>
                        {convertToRawDate(selectedTicket.ExpectedStartDate)} to{" "}
                        {convertToRawDate(selectedTicket.ExpectedEndDate)}
                      </Typography>
                      <Typography variant="caption" display="block">
                        {selectedTicket.LeadTime}
                      </Typography>
                    </Box>

                    {selectedTicket.ActualStartDate && (
                      <Box>
                        <Typography variant="subtitle2">
                          Actual Dates
                        </Typography>
                        <Typography>
                          {convertToRawDate(selectedTicket.ActualStartDate)} to{" "}
                          {convertToRawDate(selectedTicket.ActualEndDate) ||
                            "Ongoing"}
                        </Typography>
                      </Box>
                    )}

                    <Box>
                      <Typography variant="subtitle2">Created</Typography>
                      <Typography>
                        {convertToRawDate(selectedTicket.createdAt)}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>

              {selectedTicket._Approvals &&
                selectedTicket._Approvals.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />

                    {/* Approval History */}
                    <Typography variant="h6" gutterBottom color="#d32f2f">
                      Approval History
                    </Typography>

                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#ffebee" }}>
                            <TableCell>Level</TableCell>
                            <TableCell>Approver</TableCell>
                            <TableCell>Date</TableCell>
                            <TableCell>Comment</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedTicket._Approvals.map((approval, index) => (
                            <TableRow key={index}>
                              <TableCell>{approval.ApprovalType}</TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Person
                                    sx={{ mr: 1, color: "action.active" }}
                                  />
                                  {`${approval?._Approver?.FirstName} ${approval?._Approver?.LastName}`}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {convertToRawDate(approval.createdAt)}
                              </TableCell>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <Comment
                                    sx={{ mr: 1, color: "action.active" }}
                                  />
                                  {approval.Comment}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    approval.Status ? "Approved" : "Reject"
                                  }
                                  color="success"
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}

              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleCloseDetails}
                  color="error"
                >
                  Close
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default UnresolvedTickets;
